import React, { useEffect, useState } from "react";
import { Loader2, Zap, TrendingDown, AlertTriangle, Brain } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const FMT_EUR = new Intl.NumberFormat("ca-ES", {
  style: "currency", currency: "EUR", minimumFractionDigits: 2,
});
const FMT_NUM = new Intl.NumberFormat("ca-ES");

/**
 * Super-admin telemetry card for the cost-optimized OCR pipeline.
 *
 * Shows how much the Tesseract-first strategy is saving versus calling
 * GPT-4o on every invoice. Lets the owner pick a time window (7/30/90 d.).
 */
export default function AdminOcrStats() {
  const { lang } = useI18n();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/admin/ocr-stats?days=${days}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const t = (es, ca) => (lang === "es" ? es : ca);

  return (
    <section className="card-surface p-6" data-testid="admin-ocr-stats">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Brain className="text-violet-400" size={20} />
          <h3 className="text-lg font-semibold text-white">
            {t("Costes OCR (Tesseract vs GPT-4o)", "Costos OCR (Tesseract vs GPT-4o)")}
          </h3>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`ocr-stats-days-${d}`}
              className={`px-2.5 py-1 rounded-md border transition ${
                days === d
                  ? "bg-violet-500/20 border-violet-400/50 text-violet-100"
                  : "bg-white/[0.02] border-white/10 text-slate-400 hover:bg-white/[0.05]"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-center py-10 text-slate-400">
          <Loader2 size={18} className="mx-auto animate-spin" />
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-rose-300 text-sm py-4">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* KPI cards row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <KpiCard
              label={t("Facturas procesadas", "Factures processades")}
              value={FMT_NUM.format(data.totals.invoices)}
              accent="slate"
              testid="ocr-kpi-invoices"
            />
            <KpiCard
              label={t("Gratis (Tesseract)", "Gratis (Tesseract)")}
              value={`${FMT_NUM.format(data.totals.free_calls)} (${data.totals.tesseract_success_pct}%)`}
              accent="emerald"
              icon={<Zap size={12} />}
              testid="ocr-kpi-free"
            />
            <KpiCard
              label={t("Con coste (GPT-4o)", "Amb cost (GPT-4o)")}
              value={`${FMT_NUM.format(data.totals.paid_calls)}`}
              accent="amber"
              testid="ocr-kpi-paid"
            />
            <KpiCard
              label={t("Coste estimado", "Cost estimat")}
              value={FMT_EUR.format(data.totals.estimated_cost_eur)}
              accent="violet"
              testid="ocr-kpi-cost"
            />
          </div>

          {/* Savings vs all-cloud baseline */}
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 mb-5 flex items-start gap-3"
               data-testid="ocr-savings-card">
            <TrendingDown className="text-emerald-400 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <div className="text-sm text-emerald-100">
                {t(
                  `Estás ahorrando ${FMT_EUR.format(data.totals.saved_eur)} (${data.totals.tesseract_success_pct}%) en los últimos ${data.period_days} días gracias a Tesseract.`,
                  `Estàs estalviant ${FMT_EUR.format(data.totals.saved_eur)} (${data.totals.tesseract_success_pct}%) els darrers ${data.period_days} dies gràcies a Tesseract.`
                )}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {t(
                  `Sin la estrategia Tesseract-first, hubieras gastado ${FMT_EUR.format(data.totals.would_have_cost_eur)} (todos via GPT-4o).`,
                  `Sense l'estratègia Tesseract-first, hauries gastat ${FMT_EUR.format(data.totals.would_have_cost_eur)} (tot via GPT-4o).`
                )}
              </div>
            </div>
          </div>

          {/* Daily series chart (simple bar visualization) */}
          {data.daily_series && data.daily_series.length > 0 && (
            <div className="mb-5">
              <h4 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                {t("Volumen diario", "Volum diari")}
              </h4>
              <DailyChart series={data.daily_series} />
              <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500/70" />
                  {t("Tesseract", "Tesseract")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-500/70" />
                  GPT-4o
                </span>
              </div>
            </div>
          )}

          {/* Top companies by cloud usage */}
          {data.top_companies && data.top_companies.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                {t("Top empresas por coste OCR", "Top empreses per cost OCR")}
              </h4>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left py-1.5">{t("Empresa", "Empresa")}</th>
                    <th className="text-right py-1.5">{t("Llamadas GPT-4o", "Crides GPT-4o")}</th>
                    <th className="text-right py-1.5">{t("Coste", "Cost")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_companies.map((c, idx) => (
                    <tr key={idx} className="border-t border-white/5"
                        data-testid={`ocr-top-company-${idx}`}>
                      <td className="py-1.5 text-slate-200">{c.company_name}</td>
                      <td className="text-right py-1.5 font-mono text-amber-300">
                        {FMT_NUM.format(c.cloud_calls)}
                      </td>
                      <td className="text-right py-1.5 font-mono text-violet-300">
                        {FMT_EUR.format(c.estimated_cost_eur)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

const ACCENT_MAP = {
  slate: "text-slate-200 border-white/10",
  emerald: "text-emerald-300 border-emerald-500/30",
  amber: "text-amber-300 border-amber-500/30",
  violet: "text-violet-300 border-violet-500/30",
};

function KpiCard({ label, value, accent = "slate", icon, testid }) {
  return (
    <div className={`rounded-xl border p-3 bg-white/[0.02] ${ACCENT_MAP[accent] || ACCENT_MAP.slate}`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function DailyChart({ series }) {
  // Compute max for scaling so the tallest bar fills 100% height
  const max = Math.max(1, ...series.map((d) => d.free + d.paid));
  return (
    <div className="flex items-end gap-0.5 h-24 rounded-lg bg-white/[0.02] border border-white/10 p-2"
         data-testid="ocr-daily-chart">
      {series.map((d) => {
        const total = d.free + d.paid;
        const heightPct = Math.max(2, (total / max) * 100);
        const freePct = total > 0 ? (d.free / total) * 100 : 0;
        return (
          <div
            key={d.day}
            className="flex-1 flex flex-col-reverse rounded-sm overflow-hidden"
            style={{ height: `${heightPct}%`, minWidth: 4 }}
            title={`${d.day} · ${d.free} gratis · ${d.paid} GPT-4o`}
            data-testid={`ocr-day-${d.day}`}
          >
            <div className="bg-emerald-500/70" style={{ height: `${freePct}%` }} />
            <div className="bg-amber-500/70 flex-1" />
          </div>
        );
      })}
    </div>
  );
}
